/**
 Copyright Highway9 Networks Inc.
 */
import { CARRIER_MODE, COLOR } from "../../../constants";
import Radio, { RadioStatus, RadioX } from "../../../types/radio";
import { getRadioStatus } from "../radioHelper";
import moment from "moment";

export const getEdgeGaugeData = (data: Radio[]) => {
  const status = {
    connected: 0,
    disconnected: 0,
    unknown: 0,
    partiallyConnected: 0,
    thirdParty: 0,
  };

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const state = item.runtimeInfo?.edgeStatus?.overallEdgeStatus;

    switch (state) {
      case RadioStatus.GOOD:
        status.connected++;
        break;
      case RadioStatus.BAD:
        status.disconnected++;
        break;
      case RadioStatus.UNKNOWN:
        status.unknown++;
        break;
      case RadioStatus.PARTIALLY_GOOD:
        status.partiallyConnected++;
        break;
      case RadioStatus.THIRDPARTY:
        status.thirdParty++;
        break;
      default:
        break;
    }
  }

  return status;
};

export const getRFGaugeData = (data: Radio[]) => {
  const status = {
    good: 0,
    bad: 0,
    needAttention: 0,
    off: 0,
    unknown: 0,
    thirdParty: 0,
  };

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const state = getRFStatus(item);

    switch (state) {
      case "good":
        status.good++;
        break;
      case "bad":
        status.bad++;
        break;
      case "needAttention":
        status.needAttention++;
        break;
      case "off":
        status.off++;
        break;
      case "unknown":
        status.unknown++;
        break;
      case "thirdParty":
        status.thirdParty++;
        break;
      default:
        break;
    }
  }

  return status;
};

export const getRFStatus = (radio: Radio) => {
  const cells = radio.runtimeInfo?.cellParameters;
  const configCells = radio.cellParameters;
  const isDualCarrier = radio.carrierMode === CARRIER_MODE.DUAL_CARRIER;

  // if is third party
  if (!radio.isManaged) {
    return "thirdParty";
  }

  // if is unknown
  if (!radio.hasOwnProperty("serialNumber") || cells?.length === 0) {
    return "unknown";
  }

  let onCount = 0;
  const isBad = new Set<boolean>([]);
  const isRFmatched = new Set<boolean>([]);
  cells?.forEach((_cell, i) => {
    if(!_cell) return;
    // Ignore second cell parameter in runtimeInfo for single carrier
    if (radio.carrierMode === CARRIER_MODE.SINGLE_CARRIER && i > 0) return

    const RFenabled = _cell.rfEnabled;
    const configRFenabled = configCells?.[i]?.rfEnabled;

    if (RFenabled && configRFenabled) {
      onCount += 1;
    }

    if (RFenabled !== configRFenabled) {
      isBad.add(true);
      isRFmatched.add(false);
    } else {
      isBad.add(false);
      isRFmatched.add(true);
    }
  });

  // console.log(radio.name,  "isBad", isBad)

  // if is bad
  // IF config and runtime is not matched for all cell
  if (!isBad.has(false)) return "bad";

  // if is good / on
  if ((onCount === 1 && radio.carrierMode === CARRIER_MODE.SINGLE_CARRIER) || onCount === cells?.length) {
    return "good";
  }

  // if is off
  else if (onCount === 0 || !isRFmatched.has(false)) {
    return "off";
  }
  // if is it is DUAL CARRIER and partially on
  else if (isDualCarrier) {
    return "bad";
  }
  // if is partially on / need attention
  else {
    return "needAttention";
  }
};

export const getDualCarrierRFStatus = (radio: Radio, index: number) => {
  const cells = radio.runtimeInfo?.cellParameters;
  const configCells = radio.cellParameters;

  // Validate inputs
  if (!radio.isManaged) {
    return "thirdParty";
  }

  if (!radio.hasOwnProperty("serialNumber") || !cells || cells.length === 0) {
    return "unknown";
  }

  // Ensure index is valid
  if (index < 0 || index >= cells.length) {
    return "unknown";
  }

  const cell = cells[index];
  if (!cell) {
    return "unknown";
  }

  const RFenabled = cell.rfEnabled;
  const configRFenabled = configCells?.[index]?.rfEnabled;

  // If RF is enabled in both runtime and config
  if (RFenabled && configRFenabled) {
    return "good";
  }

  // If RF is disabled in both runtime and config
  if (!RFenabled && !configRFenabled) {
    return "off";
  }

  // If there's a mismatch between runtime and config
  if (RFenabled !== configRFenabled) {
    return "bad";
  }

  // If partially enabled
  if (RFenabled || configRFenabled) {
    return "needAttention";
  }

  return "unknown";
};

export const getVMCGaugeData = (data: Radio[]) => {
  const status = {
    active: 0,
    inactive: 0,
    unknown: 0,
    thirdParty: 0,
  };

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const state = item.runtimeInfo?.cloudStatus;

    switch (state) {
      case RadioStatus.GOOD:
        status.active++;
        break;
      case RadioStatus.BAD:
        status.inactive++;
        break;
      case RadioStatus.UNKNOWN:
        status.unknown++;
        break;
      case RadioStatus.THIRDPARTY:
        status.thirdParty++;
        break;
      default:
        break;
    }
  }

  return status;
};

const MINS = 60;
const HOUR = 60 * MINS;
const DAY = 24 * HOUR;
const TEN_DAYS = 10 * DAY;
const THIRTY_DAYS = 30 * DAY;
const NINETY_DAYS = 90 * DAY;

export const getDeviceRunningStatus = (item: Radio) => {
  if (!item.runtimeInfo?.systemStartTime) return;

  const uptime = moment().diff(moment.unix(item.runtimeInfo.systemStartTime), "seconds");

  if (uptime < TEN_DAYS) {
    return 1;
  } else if (uptime < THIRTY_DAYS) {
    return 2;
  } else if (uptime < NINETY_DAYS) {
    return 3;
  } else {
    return 4;
  }
};

export const getDeviceRunningData = (data: Radio[]) => {
  const bar = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
  };

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const state = getDeviceRunningStatus(item);

    switch (state) {
      case 1:
        bar[1]++;
        break;
      case 2:
        bar[2]++;
        break;
      case 3:
        bar[3]++;
        break;
      case 4:
        bar[4]++;
        break;
      default:
        break;
    }
  }
  return bar;
};

export const getOverallHealthData = (data: Radio[]) => {
  const status = {
    green: 0,
    red: 0,
    orange: 0,
    unknown: 0,
    thirdparty: 0,
  };

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const state = getRadioStatus(item as RadioX);

    switch (state) {
      case COLOR.GREEN:
        status.green++;
        break;
      case COLOR.RED:
        status.red++;
        break;
      case COLOR.ORANGE:
        status.orange++;
        break;
      case COLOR.UNKNOWN:
        status.unknown++;
        break;
      case COLOR.BLUE:
        status.thirdparty++;
        break;
      default:
        break;
    }
  }

  return status;
};

export function getOverallRadioHealth(radios: RadioX[]) {
  let green = 0;
  let orange = 0;
  let red = 0;
  let unknown = 0;
  let thirdparty = 0;
  for (let i = 0; i < radios?.length; i++) {
    const item = radios[i];
    if (!item?.isAddedToRadioManager && !item?.isManaged) {
      thirdparty++;
    } else if (!item?.isAddedToRadioManager && item?.isManaged) {
      unknown++;
    } else if (item?.statusColor === COLOR.GREEN) {
      green++;
    } else if (item?.statusColor === COLOR.ORANGE) {
      orange++;
    } else {
      red++;
    }
  }
  return {
    green,
    orange,
    red,
    unknown,
    thirdparty,
  };
}
